import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCircle, ShareNetwork, UserPlus, FolderSimple } from '@phosphor-icons/react';

const NOTIF_ICONS = {
  project_shared: ShareNetwork,
  invitation_received: UserPlus,
  project_updated: FolderSimple,
};

const NOTIF_COLORS = {
  project_shared: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  invitation_received: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  project_updated: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-GB');
}

export default function NotificationBell() {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wrapperRef = useRef(null);
  const pollRef = useRef(null);

  async function loadNotifications() {
    try {
      const res = await apiCall('get', '/notifications?limit=20');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) { /* silent */ }
  }

  useEffect(() => {
    loadNotifications();
    pollRef.current = setInterval(loadNotifications, 30000);
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function markRead(id) {
    try {
      await apiCall('post', `/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  }

  async function markAllRead() {
    try {
      await apiCall('post', '/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  }

  function handleClick(n) {
    if (!n.read) markRead(n.id);
    if (n.link) { navigate(n.link); setOpen(false); }
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => { setOpen(!open); if (!open) loadNotifications(); }}
        className="w-[38px] h-[38px] rounded-md flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-secondary transition-colors relative"
        title="Notifications"
        data-testid="topbar-notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] bg-destructive text-white rounded-full text-[9px] font-bold flex items-center justify-center px-1 ring-2 ring-card" data-testid="notif-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[44px] w-[360px] bg-card border border-border rounded-lg shadow-dropdown z-50 overflow-hidden" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-bold">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-primary hover:underline font-medium flex items-center gap-1" data-testid="mark-all-read">
                <CheckCircle size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm font-medium">No notifications</p>
                <p className="text-xs mt-1">You're all caught up!</p>
              </div>
            ) : (
              notifications.map(n => {
                const Icon = NOTIF_ICONS[n.type] || Bell;
                const colorCls = NOTIF_COLORS[n.type] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-border/50 last:border-0 hover:bg-secondary/50 transition-colors ${!n.read ? 'bg-primary/[0.03]' : ''}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${colorCls}`}>
                      <Icon size={16} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!n.read ? 'font-semibold' : 'font-medium text-muted-foreground'}`}>{n.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{n.message}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && (
                      <span className="w-2 h-2 bg-primary rounded-full shrink-0 mt-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
